<template>
  <section>
    <div class="structured-top">
      <h1 v-mark-word>{{ $t('profile.title') }}</h1>
      <span class="lead" v-html="$t('profile.description')" />
    </div>

    <div class="structured-content">
      <table class="table structure-table no-background fixed-table">
        <tbody>
          <tr>
            <td>{{ $t('profile.labels.name') }}</td>
            <td class="main">{{ profile.name }}</td>
          </tr>

          <tr>
            <td>{{ $t('profile.labels.identityCode') }}</td>
            <td class="main">{{ profile.personId }}</td>
          </tr>

          <tr>
            <td>{{ $t('profile.labels.phone') }}</td>
            <td class="main">
              <EditableItem
                name="profile.phone"
                :value="profile.phone"
                @save="update('phone', $event)"
              />
            </td>
          </tr>

          <tr>
            <td>{{ $t('profile.labels.email') }}</td>
            <td class="main">
              <EditableItem
                type="email"
                name="profile.email"
                :value="profile.email"
                @save="update('email', $event)"
              />
              <span v-if="!profile.email" class="caption text-danger">
                <span class="icon-notification"></span>
                {{ $t('profile.notificationWarningMissingEmail') }}
              </span>
            </td>
          </tr>

          <tr>
            <td />
            <td>
              <div class="custom-control custom-checkbox">
               <Checkbox
                   id="subscribeToEmailNotificationsCheckbox"
                   :disabled="!profile.email"
                   :checked="profile.subscribesToNewNotifications"
                   @change="toggleNotificationEmailSubscription()"
               >
                 <span >
                   {{ $t('myNotifications.profile.subscribeToEmailNotifications') }}
                 </span>
               </Checkbox>
              </div>
            </td>
          </tr>

          <tr>
            <td class="align-baseline">{{ $t('profile.labels.language') }}</td>
            <td>
              <div>
                <select class="custom-select form-control w-auto h-auto" @change="updateLanguagePreference()" v-model="details.languagePreference">
                  <option v-for="language in $tm('profile.languages')" :value="language.code" :key="language.title">
                    {{ language.title }}
                  </option>
                </select>
                <div class="caption gray-600">{{ $t('profile.languageHint') }}</div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <ValidationGroup
        @submit="acceptIt"
        class="structured-content negative bordered"
        v-if="!profile.acceptsNewsletter"
      >
        <SubscriptionForm
          @change="mergeSubscription"
          :info="$t('profile.subscribeHint')"
          :labelCheckbox="$t('profile.consentCheckbox')"
        >
          <template v-slot:validationFeedback>
            <span class="invalid-feedback">{{ $t('newsletter.validation.isAgreed') }}</span>
          </template>
        </SubscriptionForm>
        <button class="float-start btn btn-default" name="agree" type="submit">
          {{ $t('profile.agree') }}
        </button>
      </ValidationGroup>
      <div v-else>
        <span class="h3">{{ $t('profile.marketingSettings') }}</span>
        <table class="table structure-table no-background mb-4 mt-2 fixed-table">
          <tbody>
            <tr>
              <td>{{ $t('profile.labels.email') }}</td>
              <td class="main">
                <EditableItem
                  type="email"
                  required
                  name="profile.subscriptionEmail"
                  :value="profile.subscriptionEmail"
                  @save="update('subscriptionEmail', $event)"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <button class="btn btn-default btn-sm mb-4" type="button" @click="update('acceptsNewsletter', false)">
          {{ $t('profile.discardNewsletter') }}
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import EditableItem from '../components/EditableItem';
import SubscriptionForm from '../components/SubscriptionForm';
import ValidationGroup from '../components/ValidationGroup';
import Checkbox from '@/components/Checkbox.vue';

export default {
  components: {
    Checkbox,
    EditableItem,
    SubscriptionForm,
    ValidationGroup,
  },
  data: () => ({
    details: {
      acceptsNewsletter: null,
      languagePreference: '',
      subscriptionEmail: '',
    },
  }),
  computed: {
    ...mapState('session', ['profile']),
  },
  created() {
    this.getUserInfo();
    if (!this.profile.personId) {
      this.redirectAfterLogin(this.$route.fullPath);
    }
    this.details.languagePreference = this.profile.languagePreference;
  },
  methods: {
    ...mapActions('session', ['updateProfile', 'getUserInfo']),
    ...mapActions('alerts', ['notify']),
    ...mapMutations('session', ['redirectAfterLogin']),

    acceptIt() {
      const { acceptsNewsletter } = this.details;

      if (acceptsNewsletter !== null && this.profile.acceptsNewsletter !== acceptsNewsletter) {
        this.update(this.details);
      }
    },

    update(name, value) {
      const params = typeof name === 'object' ? name : { [name]: value };

      if (name === 'email' && value.length === 0) { // do not allow user to remove e-mail
        this.notify({ type: 'danger', text: this.$t('profile.notificationWarningCantRemoveEmail') })
        return;
      }

      return this.updateProfile(params)
        .then(() => this.notify({ type: 'success', text: this.$t('profile.success') }));
    },

    mergeSubscription(value) {
      this.details.acceptsNewsletter = value.wantsNewsletter;
      this.details.subscriptionEmail = value.subscriptionEmail;
    },

    toggleNotificationEmailSubscription() {
      this.update({ subscribesToNewNotifications: !this.profile.subscribesToNewNotifications });
    },

    updateLanguagePreference() {
      this.update({ languagePreference: this.details.languagePreference });
    },
  },
  metaInfo() {
    return {
      title: this.$t('profile.meta.title'),
      meta: [
        { vmid: 'keywords', name: 'keywords', content: this.$t('profile.meta.keywords') },
        { vmid: 'description', name: 'description', content: this.$t('profile.meta.description') },
      ],
    };
  },
};
</script>

<style scoped lang="scss">
.details {
  background: transparent;
  border: none;
}
</style>
